import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import Navbar from "@/components/Navbar";
import { useCart } from "@/contexts/CartContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [squareEnabled, setSquareEnabled] = useState(false);

  const [form, setForm] = useState({
    customer_name: "",
    customer_phone: "",
    customer_email: "",
    order_type: "pickup",
    address: "",
    notes: "",
    payment_method: "cash",
  });

  useEffect(() => {
    if (items.length === 0) navigate("/");
    api.get("/config/square").then((r) => setSquareEnabled(r.data.enabled)).catch(() => {});
    // eslint-disable-next-line
  }, []);

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        customer_email: form.customer_email || undefined,
        address: form.order_type === "delivery" ? form.address : undefined,
        notes: form.notes || undefined,
        items,
      };
      const { data } = await api.post("/orders", payload);
      clear();
      toast.success("Order placed!");
      navigate(`/order/${data.id}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not place order");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar showCartButton={false} />
      <div className="max-w-4xl mx-auto px-6 lg:px-12 py-12">
        <button
          onClick={() => navigate("/")}
          data-testid="back-to-menu-btn"
          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-8"
        >
          <ArrowLeft className="h-4 w-4" /> Back to menu
        </button>

        <h1 className="font-display text-4xl lg:text-5xl tracking-tight font-light mb-10">Checkout</h1>

        <div className="grid lg:grid-cols-[1.5fr_1fr] gap-10">
          <form
            onSubmit={submit}
            data-testid="checkout-form"
            className="bg-white rounded-3xl border border-border/60 p-8 space-y-6"
          >
            <div>
              <Label className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Order Type</Label>
              <RadioGroup
                value={form.order_type}
                onValueChange={(v) => update("order_type", v)}
                className="grid grid-cols-2 gap-3 mt-3"
              >
                {["pickup", "delivery"].map((t) => (
                  <label
                    key={t}
                    className={`flex items-center gap-3 px-4 py-4 rounded-2xl border cursor-pointer transition-all ${
                      form.order_type === t ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                    }`}
                  >
                    <RadioGroupItem value={t} data-testid={`order-type-${t}`} />
                    <span className="font-display text-lg capitalize">{t}</span>
                  </label>
                ))}
              </RadioGroup>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <Label>Name *</Label>
                <Input
                  required
                  data-testid="customer-name-input"
                  value={form.customer_name}
                  onChange={(e) => update("customer_name", e.target.value)}
                  className="mt-1 rounded-xl"
                />
              </div>
              <div>
                <Label>Phone *</Label>
                <Input
                  required
                  data-testid="customer-phone-input"
                  value={form.customer_phone}
                  onChange={(e) => update("customer_phone", e.target.value)}
                  className="mt-1 rounded-xl"
                />
              </div>
            </div>

            <div>
              <Label>Email (optional)</Label>
              <Input
                type="email"
                data-testid="customer-email-input"
                value={form.customer_email}
                onChange={(e) => update("customer_email", e.target.value)}
                className="mt-1 rounded-xl"
              />
            </div>

            {form.order_type === "delivery" && (
              <div>
                <Label>Delivery Address *</Label>
                <Input
                  required
                  data-testid="address-input"
                  value={form.address}
                  onChange={(e) => update("address", e.target.value)}
                  className="mt-1 rounded-xl"
                />
              </div>
            )}

            <div>
              <Label>Order Notes (optional)</Label>
              <Textarea
                data-testid="order-notes-input"
                value={form.notes}
                onChange={(e) => update("notes", e.target.value)}
                className="mt-1 rounded-xl"
                placeholder="Any special instructions?"
              />
            </div>

            <div>
              <Label className="text-xs tracking-[0.25em] uppercase text-muted-foreground">Payment</Label>
              <div className="mt-3 px-4 py-4 rounded-2xl border border-border bg-muted/40">
                <p className="font-display text-lg">Pay on {form.order_type === "delivery" ? "delivery" : "pickup"}</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {squareEnabled
                    ? "Square card payment coming next — we'll wire the card form once you confirm."
                    : "Online card payment (Square) will activate as soon as your Square credentials are added."}
                </p>
              </div>
            </div>

            <Button
              type="submit"
              disabled={submitting}
              data-testid="place-order-btn"
              className="w-full rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 font-display tracking-wide"
            >
              {submitting ? "Placing order…" : `Place Order · $${subtotal.toFixed(2)}`}
            </Button>
          </form>

          <aside className="bg-white rounded-3xl border border-border/60 p-8 h-fit sticky top-28">
            <p className="text-xs tracking-[0.25em] uppercase text-muted-foreground mb-4">Your Order</p>
            <div className="space-y-3 divide-y divide-border/60">
              {items.map((item, idx) => (
                <div key={idx} className="pt-3 first:pt-0">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="font-display">{item.quantity}× {item.name}</p>
                      {item.flavors?.length > 0 && (
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {item.flavors.join(", ")}
                        </p>
                      )}
                    </div>
                    <span className="text-sm">${(item.price * item.quantity).toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-border/60 mt-5 pt-5 flex items-center justify-between">
              <span className="text-sm uppercase tracking-wide text-muted-foreground">Total</span>
              <span data-testid="checkout-total" className="font-display text-2xl text-primary">${subtotal.toFixed(2)}</span>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
